import { useEffect } from 'react';
import { ArrowRightIcon, ClockIcon, DropletsIcon, ExternalLinkIcon, LayersIcon, MoveIcon, PhoneIcon, PlugZapIcon } from 'lucide-react';
import { business, images, interiorHeroes, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const hero = interiorHeroes.coldPlunge;
const TITLE = 'Cold Plunge Tubs in Brooklyn, MI | Pegasus Spas';
const DESCRIPTION = 'See the Viking Spas Chill Therapy cold plunge at Pegasus Spas in Brooklyn, Michigan: a one-person cold plunge with its own chiller, filtration and ozone water care.';
const CANONICAL = 'https://www.pegasus-spas.com/ColdPlungeTubs.html';
const VIKING_URL = 'https://vikingspas.com/model/chill-therapy/';
const specs = [{
  label: 'Seating',
  value: 'One person'
}, {
  label: 'Dimensions',
  value: '70 x 33 x 37.5 in.'
}, {
  label: 'Water capacity',
  value: '79 gallons'
}, {
  label: 'Chiller',
  value: '120V / 60Hz / 7.5-amp mini-inverter'
}, {
  label: 'Controls',
  value: 'Digital topside'
}, {
  label: 'Filtration',
  value: 'Circulation pump and HydroPur filter'
}, {
  label: 'Water care',
  value: 'Ozone system'
}, {
  label: 'Included',
  value: 'Matching cover and step'
}];
const locationPoints = [{
  icon: LayersIcon,
  title: 'A Level, Supported Surface',
  copy: 'Filled with water, the plunge is heavy. It needs a level floor or pad that can carry it.'
}, {
  icon: DropletsIcon,
  title: 'Filling and Draining',
  copy: 'Think about where the hose will reach and where the water will go when you drain it.'
}, {
  icon: PlugZapIcon,
  title: 'Power',
  copy: 'The chiller runs on 120V. Have a qualified electrician confirm the outlet and circuit.'
}, {
  icon: MoveIcon,
  title: 'Room Around It',
  copy: 'Leave space to step in and out, and to reach the tub for care and service.'
}];
const faqs = [{
  question: 'Where can it go?',
  answer: 'Tell us where you would like to put it. We can help you review the space, floor, water access, power and drainage. Follow the owner\u2019s manual for placement requirements.'
}, {
  question: 'Is cold-water immersion right for everyone?',
  answer: 'No. If you have any health concerns, talk with a qualified healthcare professional before you use a cold plunge.'
}, {
  question: 'How do I find out about pricing?',
  answer: 'Call or text us, or stop by the Brooklyn showroom.'
}];
const relatedLinks = [{
  label: 'Viking Spas',
  href: routes.viking
}, {
  label: 'Explore Hot Tubs',
  href: routes.hotTubs
}, {
  label: 'Financing',
  href: routes.financing
}, {
  label: 'Specials',
  href: routes.specials
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function ColdPlungePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-920" data-mp="jrkfvh5xol3">
      {/* Intro band */}
      <section aria-labelledby="plunge-h1" className="relative w-full" data-id="element-921" data-mp="5jdxo4wzr4tw">
        <div className={`relative w-full overflow-hidden ${hero.frameClass}`} data-id="element-922" data-mp="5hjdistjngqv">
          <img src={hero.src} alt={hero.alt} className={hero.imageClass} width={hero.width} height={hero.height} decoding="async" data-id="element-923" data-mp="abdrdfmgx1i1" />
          <div className="hero-scrim-compact absolute inset-0 sm:hidden" aria-hidden="true" data-id="element-924" data-mp="jclpnxozi2c4" />
          <div className="hero-scrim absolute inset-0 hidden sm:block" aria-hidden="true" data-id="element-925" data-mp="oadegiuc18c3" />
          <span className="pointer-events-none absolute bottom-3 right-3 whitespace-nowrap rounded-[4px] border border-white/15 bg-ink/80 px-2 py-[5px] text-[11px] font-medium leading-none text-white/90 backdrop-blur-[2px] sm:text-[12px]" data-id="element-926" data-mp="altu0bxssqvp">
            Viking Spas Chill Therapy
          </span>

          <div className={`relative mx-auto flex max-w-container flex-col justify-center px-5 py-10 sm:px-8 ${hero.contentClass}`} data-id="element-927" data-mp="ff67bpx11b95">
            <p className="text-[14px] font-medium uppercase text-white sm:text-[15px]" data-id="element-928" data-mp="3iutriba58c1s">
              Viking Spas Chill Therapy
            </p>
            <h1 id="plunge-h1" className="mt-3 max-w-3xl text-h1-mobile font-semibold text-white lg:text-[48px] lg:leading-[56px]" data-id="element-929" data-mp="1n88q90xyxr8">
              Cold Plunge Tubs for At-Home Wellness
            </h1>
            <p className="mt-4 max-w-2xl text-body text-white sm:text-body-lg" data-id="element-930" data-mp="182ivu5mqzt53">
              Chill Therapy is a one-person cold plunge with its own chiller, filtration and water
              care, made to fit a home gym, garage or wellness corner.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-931" data-mp="1kmpibx1epvos">
              <SiteLink to={routes.contact} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-932" data-mp="16mett0nkkt6s">
                Ask About Chill Therapy
              </SiteLink>
              <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white bg-white/10 px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-933" data-mp="23auhssb16uhs">
                Visit the Showroom
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section aria-labelledby="specs-heading" className="w-full bg-water" data-id="element-934" data-mp="2vym4xbbxeq17">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-935" data-mp="2rwisajditfec">
          <h2 id="specs-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-936" data-mp="2ruee98lwr00c">
            Chill Therapy Specifications
          </h2>

          <dl className="mt-7 grid grid-cols-1 gap-x-8 sm:grid-cols-2 lg:grid-cols-4" data-id="element-937" data-mp="2hy2xo6fabo03">
            {specs.map(spec => <div key={spec.label} className="border-t border-ink/15 py-4" data-id="element-938" data-mp="288jipbup0eai">
                <dt className="text-[14px] font-bold uppercase text-ink/60" data-id="element-939" data-mp="286f2pvjirotv">{spec.label}</dt>
                <dd className="mt-1 text-[17px] font-medium leading-6 text-ink" data-id="element-940" data-mp="24769qswazuzd">{spec.value}</dd>
              </div>)}
          </dl>

          <a href={VIKING_URL} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex min-h-[44px] items-center gap-1.5 text-[15.5px] font-medium text-ink/70 underline decoration-ink/30 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-941" data-mp="1u9a1c8ur3e4y">
            Source: Viking Spas
            <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-942" data-mp="1p3ws34cwgbb8" />
          </a>
        </div>
      </section>

      {/* Plan the location */}
      <section aria-labelledby="location-heading" className="w-full bg-white" data-id="element-943" data-mp="1u1y81gf13uih">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-944" data-mp="wfr4m9ugo87p">
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-945" data-mp="11ebyzsn17xo8">
            <div className="lg:col-span-5" data-id="element-946" data-mp="16df39av6tzv5">
              <h2 id="location-heading" className="text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-947" data-mp="16cdp2xivkx97">
                Planning the Spot
              </h2>
              <div className="mt-6 aspect-[4/3] w-full overflow-hidden rounded-card" data-id="element-948" data-mp="dnyaud7kiiyx">
                <img src={images.pathColdPlunge} alt="Cold plunge tub positioned against a garage wall with open floor space beside it" className="h-full w-full object-cover [object-position:35%_70%]" loading="lazy" decoding="async" data-id="element-949" data-mp="imswro0u0nn9" />
              </div>
            </div>

            <div className="lg:col-span-7" data-id="element-950" data-mp="21sujrayi9uh7">
              <ul className="grid grid-cols-1 gap-x-10 gap-y-7 sm:grid-cols-2" data-id="element-951" data-mp="21s2nrz0zxuqe">
                {locationPoints.map(point => {
                const Icon = point.icon;
                return <li key={point.title} data-id="element-952" data-mp="2brbfsmcawza4">
                      <Icon className="h-6 w-6 text-pine" aria-hidden="true" data-id="element-953" data-mp="2pjhus53px07z" />
                      <h3 className="mt-3 text-[19px] font-semibold leading-7 text-ink" data-id="element-954" data-mp="2kjvhjvnw3jz6">
                        {point.title}
                      </h3>
                      <p className="mt-1.5 text-[16px] leading-6 text-ink/75" data-id="element-955" data-mp="2zhxp0gy1kplp">{point.copy}</p>
                    </li>;
              })}
              </ul>

              <dl className="mt-10 space-y-6 border-t border-ink/10 pt-6" data-id="element-956" data-mp="3e3bei1898ysj">
                {faqs.map(faq => <div key={faq.question} data-id="element-957" data-mp="3i4exuxpihcyi">
                    <dt className="text-[18px] font-semibold leading-7 text-ink" data-id="element-958" data-mp="3i33tavo8s16c">{faq.question}</dt>
                    <dd className="mt-1.5 text-[16px] leading-6 text-ink/75" data-id="element-959" data-mp="3481efj1f31he">{faq.answer}</dd>
                  </div>)}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Showroom CTA */}
      <section aria-labelledby="see-heading" className="w-full bg-ink" data-id="element-960" data-mp="2z6u69ivd40a7">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-961" data-mp="2lf5yfrs5bxz1">
          <div className="max-w-3xl" data-id="element-962" data-mp="2ld2yaexa4ogl">
            <h2 id="see-heading" className="text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-963" data-mp="2qdqxhnvnwblw">
              See Chill Therapy in Brooklyn
            </h2>
            <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-964" data-mp="2ggcilspdzuvp">
              Stop in to see it, or tell us about the space you have in mind and we&rsquo;ll help
              you plan for it.
            </p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-965" data-mp="1m24w4pc6s6xq">
            <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-966" data-mp="174cenrn2x8gw">
              <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-967" data-mp="24rushgvll0rr" />
              Location &amp; Hours
            </SiteLink>
            <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border-2 border-white/70 px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-968" data-mp="1q1nfvhs9ubyz">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-969" data-mp="2niks8wpvg7ge" />
              Call or Text {business.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-970" data-mp="72xyqvq3wvnh">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-971" data-mp="lrbxaughpxy8">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-972" data-mp="gt0ngjq73ng0">
            {relatedLinks.map(link => <li key={link.label} data-id="element-973" data-mp="3p9vr41ethbv5">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-974" data-mp="3ka93qz59yf8m">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-975" data-mp="1dlw4uxjqev1t" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}